import { Trans, useTranslation } from 'react-i18next'
import { useState } from 'react'
import clsx from 'clsx'

import styles from '../CandidateDetailsContainer.module.scss'
import { decodeDocumentName, formatDate } from '../../utils/utils'

import documentStyles from './CandidateDocumentsCard.module.scss'
import {
  AcknowledgeModal,
  Button,
  CardDashboard,
  ConfirmationModal,
  Dropzone,
  Flex,
  GalleryPreview,
  Icon,
  IllustratedMessage,
  Label,
  Modal,
  Text,
  tokens
} from '@percent/lemonade'
import { DOCUMENT_UPLOAD_ACCEPT, MAX_GRANT_DOCUMENT_FILES } from '@percent/domain/grants'
import { CandidateDocumentsCardProps } from './CandidateDocumentsCard.types'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useMutation } from '@percent/utility'

export function CandidateDocumentsCard({
  documents,
  candidate,
  onDocsSubmitted
}: Readonly<CandidateDocumentsCardProps>) {
  const { t } = useTranslation()
  const { userCan } = usePermissionCheck()
  const canUploadDocuments = userCan('uploadGrantDocuments')
  const [modalType, setModalType] = useState<'confirmation' | 'success' | 'error' | null>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const { grantsService } = useServices()
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>()
  const sortedDocuments = documents
    .map(document => ({ ...document, name: decodeDocumentName(document.name) }))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  const selectedDocument = sortedDocuments.find(({ id }) => id === selectedDocumentId) ?? sortedDocuments[0]

  const [{ isPending: isUploadDocumentsPending }, { mutateAsync: uploadDocuments }] = useMutation(
    grantsService.uploadCandidateDocuments,
    () => {
      setModalType('success')
      setSelectedFiles([])
      onDocsSubmitted?.()
    },
    () => setModalType('error')
  )

  const handleUploadDocuments = async () => {
    if (!selectedFiles.length || isUploadDocumentsPending) return
    const fd = new FormData()
    selectedFiles.forEach((file, i) => fd.append(`file${i}`, file))
    try {
      await uploadDocuments({ candidateId: candidate.id, formData: fd, saveToDb: true })
    } catch (e) {
      setModalType('error')
    }
  }

  const orgName = candidate.organisation.name

  const closeModal = () => setModalType(null)

  const successModal = modalType === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={t('grants.candidateDetails.documents.acknowledgeModal.success.title')}
      description={
        <Trans
          i18nKey="grants.candidateDetails.documents.acknowledgeModal.success.description"
          components={{
            b: <b />
          }}
          values={{ organization: orgName }}
        />
      }
      buttonText={t('button.close')}
      handleClose={closeModal}
      viewTestId="upload-documents-success-modal"
      buttonTestId="close-upload-documents-success-modal"
    />
  )

  const errorModal = modalType === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('grants.candidateDetails.documents.acknowledgeModal.error.title')}
      description={
        <Trans
          i18nKey="grants.candidateDetails.documents.acknowledgeModal.error.description"
          components={{
            b: <b />
          }}
          values={{ organization: orgName }}
        />
      }
      buttonText={t('button.close')}
      handleClose={closeModal}
      viewTestId="upload-documents-error-modal"
      buttonTestId="close-upload-documents-error-modal"
    />
  )

  return (
    <Flex direction="column" data-testid="candidate-documents-card" className={styles.cardCtn} gap={16}>
      <CardDashboard
        title={documents.length > 0 ? t('grants.candidateDetails.documents.title') : undefined}
        data-testid="documents-card"
        action={
          canUploadDocuments ? (
            <Button
              variant="secondary"
              size="small"
              data-testid="upload-docs-main-btn"
              onClick={() => setModalType('confirmation')}
            >
              {t('button.uploadDocuments')}
            </Button>
          ) : null
        }
      >
        {selectedDocument ? (
          <Flex gap={16} align="flex-start">
            <ul className={documentStyles.documentList} data-testid="candidate-documents-list">
              {sortedDocuments.map(({ id, name, url, createdAt }) => (
                <li
                  key={id}
                  className={clsx(
                    documentStyles.documentListItem,
                    id === selectedDocument.id && documentStyles.selected
                  )}
                >
                  <button
                    className={documentStyles.selectDocumentButton}
                    type="button"
                    title={name}
                    aria-current={id === selectedDocument.id}
                    onClick={() => setSelectedDocumentId(id)}
                    data-testid={`candidate-document-${id}`}
                  >
                    <Text as="p" size="small" fontWeight="medium" overflowEllipsis>
                      {name}
                    </Text>
                    <Text size="small" color={tokens.colors.contentSecondary}>
                      {formatDate(createdAt)}
                    </Text>
                  </button>
                  <a
                    className={documentStyles.downloadLink}
                    href={url}
                    download={name}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={t('grants.candidateDetails.documents.preview.download')}
                    title={t('grants.candidateDetails.documents.preview.download')}
                    data-testid={`candidate-document-download-${id}`}
                  >
                    <Icon name="download" size="m" color="inherit" />
                  </a>
                </li>
              ))}
            </ul>
            <Flex flex={1} miw={0}>
              <GalleryPreview
                key={selectedDocument.url}
                url={selectedDocument.url}
                wheelDisabled
                documentPreviewLabels={{
                  cannotPreview: t('grants.candidateDetails.documents.preview.cannotPreview'),
                  download: t('grants.candidateDetails.documents.preview.download')
                }}
              />
            </Flex>
          </Flex>
        ) : (
          <IllustratedMessage
            title={t('grants.candidateDetails.documents.noDocuments.title')}
            description={t('grants.candidateDetails.documents.noDocuments.description')}
            illustration="no-grant-candidates"
          />
        )}
      </CardDashboard>

      {modalType && (
        <Modal open={!!modalType} onClose={closeModal} data-testid="upload-documents-modal">
          {successModal || errorModal || (
            <ConfirmationModal
              title={t('grants.candidateDetails.documents.confirmationModal.title')}
              primaryButtonText={t('button.uploadDocuments')}
              secondaryButtonText={t('button.cancel')}
              handleSubmit={() => handleUploadDocuments()}
              handleClose={closeModal}
              loading={isUploadDocumentsPending}
              disabled={selectedFiles.length === 0}
              primaryBtnTestId="confirm-upload-documents-button"
              secondaryBtnTestId="cancel-upload-documents-button"
            >
              <Trans
                i18nKey="grants.candidateDetails.documents.confirmationModal.description"
                components={{
                  b: <b />
                }}
                values={{ organization: orgName }}
              />
              <Flex direction="column" w="100%" pt="16px">
                <Label necessity="required" htmlFor={undefined}>
                  {t('grants.candidateDetails.documents.confirmationModal.label')}
                </Label>
                <Dropzone
                  title={t('grants.candidateDetails.documents.confirmationModal.dropzone.title')}
                  description={t('grants.candidateDetails.documents.confirmationModal.dropzone.description', {
                    maxFiles: MAX_GRANT_DOCUMENT_FILES
                  })}
                  files={selectedFiles}
                  onChange={files => setSelectedFiles(files)}
                  accept={DOCUMENT_UPLOAD_ACCEPT}
                  maxFiles={MAX_GRANT_DOCUMENT_FILES}
                  testId="candidate-docs-dropzone"
                />
              </Flex>
            </ConfirmationModal>
          )}
        </Modal>
      )}
    </Flex>
  )
}
